import React, { useEffect, useState } from "react";
import { Send, Trash2, Loader2, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function VNComments({ code, onCount }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/vn/play/${code}/comments`).then(({ data }) => { setComments(data.comments || []); onCount?.((data.comments || []).length); }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [code]);

  const add = async () => {
    const t = text.trim();
    if (!t || posting) return;
    if (!user) { toast.error("Log in to comment."); return; }
    setPosting(true);
    try {
      const { data } = await api.post(`/vn/play/${code}/comments`, { text: t });
      setComments((c) => { const n = [data, ...c]; onCount?.(n.length); return n; }); setText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post that."); }
    finally { setPosting(false); }
  };
  const del = async (cid) => {
    try { await api.delete(`/vn/play/${code}/comments/${cid}`); setComments((c) => { const n = c.filter((x) => x.id !== cid); onCount?.(n.length); return n; }); }
    catch { toast.error("Couldn't delete."); }
  };

  return (
    <div data-testid="vn-comments">
      <div className="flex items-center gap-2 mb-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder={user ? "Add a comment…" : "Log in to comment"}
          disabled={!user} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); add(); } }}
          data-testid="vn-comment-input"
          className="flex-1 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm resize-none disabled:opacity-60" />
        <button onClick={add} disabled={!text.trim() || posting || !user} data-testid="vn-comment-send"
          className="w-10 h-10 grid place-items-center rounded-full bg-[hsl(var(--primary))] text-white hover:opacity-90 disabled:opacity-50 shrink-0">
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      {loading ? (
        <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--muted-foreground))]" /></div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center flex flex-col items-center gap-2"><MessageCircle className="w-6 h-6" /> No comments yet — start the conversation!</p>
      ) : (
        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2.5" data-testid={`vn-comment-${c.id}`}>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1f6feb] to-[#a855f7] grid place-items-center text-white text-xs font-bold shrink-0 overflow-hidden">
                {c.avatar ? <img src={c.avatar} alt="" className="w-full h-full object-cover" /> : (c.author_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold">{c.author_name}</p>
                <p className="text-sm break-words">{c.text}</p>
              </div>
              {user && c.user_id === user.id && (
                <button onClick={() => del(c.id)} data-testid={`vn-comment-del-${c.id}`} className="w-7 h-7 grid place-items-center rounded-full text-rose-500 hover:bg-rose-500/10 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
